import { onError as baseCreateErrorLink, type ErrorHandler } from '@apollo/client/link/error';
import { useErrorBoundary } from 'react-error-boundary';
import { sentry } from '~features/logging';
import { shouldIgnoreNetworkError, shouldIgnoreGraphQLError } from './utils';

type CreateErrorLinkOptions = {
  onError?: ErrorHandler;
};

export const createErrorLink = ({ onError }: CreateErrorLinkOptions = {}) =>
  baseCreateErrorLink((res) => {
    const { networkError, operation, forward, graphQLErrors } = res;
    const { ignoreErrorLink } = operation.getContext();

    if (
      ignoreErrorLink ||
      shouldIgnoreNetworkError(networkError) ||
      shouldIgnoreGraphQLError(graphQLErrors)
    ) {
      return forward(operation);
    }

    return onError?.(res);
  });

export const useErrorLink = () => {
  const { showBoundary } = useErrorBoundary();

  const onError: ErrorHandler = ({ networkError, graphQLErrors, operation, response }) => {
    if (networkError) {
      sentry.captureException(networkError, {
        contexts: {
          gql: {
            operationName: operation.operationName,
            errors: response?.errors,
            extensions: response?.extensions,
          },
        },
      });
      showBoundary(networkError);
    } else {
      graphQLErrors?.forEach((error) => {
        if (error.message.toLowerCase().includes('lambda invoke')) {
          showBoundary(new Error(error.message));
        }
      });
    }
  };

  return createErrorLink({ onError });
};
